import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { Password } from 'primereact/password';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { stanjeRobeBrandsAPI, stanjeRobeCustomersAPI } from '../services/api';
import { StanjeRobeBrand, StanjeRobeCustomer } from '../types';
import { showCustomAlert } from './CustomAlert';
import { CUSTOMER_LANGUAGE_OPTIONS } from '../i18n/languages';
import { AppLanguage } from '../i18n/types';

const StanjeRobeCustomers: React.FC = () => {
  const [customers, setCustomers] = useState<StanjeRobeCustomer[]>([]);
  const [brands, setBrands] = useState<StanjeRobeBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<StanjeRobeCustomer | null>(null);
  const [error, setError] = useState('');
  const [createLogin, setCreateLogin] = useState(true);
  const [form, setForm] = useState({
    name: '',
    brandId: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    emailLanguage: 'en' as AppLanguage
  });

  const brandOptions = useMemo(() => brands.map((b) => ({ label: b.name, value: b._id })), [brands]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([stanjeRobeBrandsAPI.getAll(), stanjeRobeCustomersAPI.getAll()]);
      setBrands(b);
      setCustomers(c);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri učitavanju');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setCreateLogin(true);
    setForm({
      name: '', brandId: brands[0]?._id || '', contactPerson: '', phone: '', email: '',
      address: '', notes: '', username: '', password: '', firstName: '', lastName: '',
      emailLanguage: 'en'
    });
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (c: StanjeRobeCustomer) => {
    setEditing(c);
    setCreateLogin(false);
    const brandId = typeof c.brandId === 'string' ? c.brandId : c.brandId?._id || '';
    const linkedUser = c.userId && typeof c.userId !== 'string' ? c.userId : null;
    setForm({
      name: c.name,
      brandId,
      contactPerson: c.contactPerson || '',
      phone: c.phone || '',
      email: c.email || '',
      address: c.address || '',
      notes: c.notes || '',
      username: '', password: '', firstName: '', lastName: '',
      emailLanguage: (linkedUser?.emailLanguage as AppLanguage) || 'en'
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Naziv kupca je obavezan'); return; }
    if (!form.brandId) { setError('Brend je obavezan'); return; }
    if (!editing && createLogin) {
      if (!form.username.trim()) { setError('Korisničko ime je obavezno za login'); return; }
      if (!form.password || form.password.length < 6) { setError('Lozinka mora imati najmanje 6 karaktera'); return; }
    }
    try {
      if (editing) {
        await stanjeRobeCustomersAPI.update(editing._id, {
          name: form.name.trim(),
          brandId: form.brandId,
          contactPerson: form.contactPerson,
          phone: form.phone,
          email: form.email,
          address: form.address,
          notes: form.notes,
          ...(editing.userId ? { emailLanguage: form.emailLanguage } : {})
        });
      } else {
        await stanjeRobeCustomersAPI.create({
          name: form.name.trim(),
          brandId: form.brandId,
          contactPerson: form.contactPerson,
          phone: form.phone,
          email: form.email,
          address: form.address,
          notes: form.notes,
          ...(createLogin ? {
            username: form.username.trim(),
            password: form.password,
            firstName: form.firstName.trim() || form.name.trim(),
            lastName: form.lastName.trim() || 'Kupac',
            emailLanguage: form.emailLanguage
          } : {})
        });
      }
      setDialogVisible(false);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Greška pri čuvanju');
    }
  };

  const handleDelete = (c: StanjeRobeCustomer) => {
    showCustomAlert.confirm(`Obrisati kupca "${c.name}"?`, async () => {
      try {
        await stanjeRobeCustomersAPI.delete(c._id);
        await load();
      } catch (err: any) {
        showCustomAlert.error(err?.response?.data?.message || 'Greška pri brisanju');
      }
    });
  };

  const loginBody = (row: StanjeRobeCustomer) => {
    const u = row.userId;
    if (!u || typeof u === 'string') return <Tag value="Bez naloga" severity="warning" />;
    return <Tag value={u.username || 'Povezan'} severity="success" icon="pi pi-user" />;
  };

  return (
    <div className="p-3 md:p-4">
      <Card title="Kupci" subTitle="Svaki kupac mora biti povezan sa brendom. Customer vidi samo stanje svog brenda.">
        {error && <Message severity="error" text={error} className="mb-3" />}
        <div className="flex justify-content-end mb-3">
          <Button label="Dodaj kupca" icon="pi pi-plus" onClick={openCreate} disabled={!brands.length} />
        </div>
        <DataTable value={customers} loading={loading} paginator rows={15} emptyMessage="Nema kupaca.">
          <Column field="name" header="Naziv" sortable />
          <Column field="brandName" header="Brend" sortable />
          <Column header="Login nalog" body={loginBody} />
          <Column field="contactPerson" header="Kontakt" />
          <Column field="phone" header="Telefon" />
          <Column
            header="Akcije"
            body={(row: StanjeRobeCustomer) => (
              <div className="flex gap-2">
                <Button icon="pi pi-pencil" rounded text onClick={() => openEdit(row)} />
                <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => handleDelete(row)} />
              </div>
            )}
            style={{ width: 120 }}
          />
        </DataTable>
      </Card>

      <Dialog header={editing ? 'Izmeni kupca' : 'Novi kupac'} visible={dialogVisible} onHide={() => setDialogVisible(false)} style={{ width: '600px' }}>
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Naziv kupca *</label>
            <InputText value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Brend *</label>
            <Dropdown value={form.brandId} options={brandOptions} onChange={(e) => setForm({ ...form, brandId: e.value })} className="w-full" />
          </div>
          <div className="field col-12 md:col-6">
            <label>Kontakt osoba</label>
            <InputText value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Telefon</label>
            <InputText value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="field col-12">
            <label>Email</label>
            <InputText value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="field col-12">
            <label>Adresa</label>
            <InputText value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="field col-12">
            <label>Napomena</label>
            <InputTextarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} autoResize />
          </div>
          {!editing && (
            <>
              <div className="col-12 flex align-items-center gap-2 mt-2">
                <InputSwitch checked={createLogin} onChange={(e) => setCreateLogin(e.value ?? true)} />
                <label className="mb-0">Kreiraj login nalog (uloga: Customer)</label>
              </div>
              {createLogin && (
                <>
                  <div className="field col-12 md:col-6">
                    <label>Jezik aplikacije za kupca *</label>
                    <Dropdown
                      value={form.emailLanguage}
                      options={CUSTOMER_LANGUAGE_OPTIONS}
                      onChange={(e) => setForm({ ...form, emailLanguage: e.value })}
                      className="w-full"
                    />
                  </div>
                  <div className="field col-12 md:col-6">
                    <label>Korisničko ime *</label>
                    <InputText value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
                  </div>
                  <div className="field col-12 md:col-6">
                    <label>Lozinka *</label>
                    <Password value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} toggleMask feedback={false} className="w-full" inputClassName="w-full" />
                  </div>
                  <div className="field col-12 md:col-6">
                    <label>Ime</label>
                    <InputText value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                  </div>
                  <div className="field col-12 md:col-6">
                    <label>Prezime</label>
                    <InputText value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                  </div>
                </>
              )}
            </>
          )}
          {editing && editing.userId && (
            <div className="field col-12 md:col-6">
              <label>Jezik aplikacije za kupca</label>
              <Dropdown
                value={form.emailLanguage}
                options={CUSTOMER_LANGUAGE_OPTIONS}
                onChange={(e) => setForm({ ...form, emailLanguage: e.value })}
                className="w-full"
              />
            </div>
          )}
          <div className="field col-12 flex justify-content-end gap-2">
            <Button label="Otkaži" outlined severity="secondary" onClick={() => setDialogVisible(false)} />
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default StanjeRobeCustomers;
